import { Component, effect, inject, input, output, ChangeDetectionStrategy } from '@angular/core';
import {
  CreditoWhatsapp,
  CreditoWhatsappPayload,
} from '../../../models/credito-whatsapp.model';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { CreditoWhatsappService } from '../../../services/creditoWhatsapp.service';
import { ToastService } from '../../../services/toast.service';

@Component({
  selector: 'app-modal-actualizar-credito-whatsapp',
  imports: [ReactiveFormsModule],
  templateUrl: './modal-actualizar-credito-whatsapp.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './modal-actualizar-credito-whatsapp.component.css',
})
export class ModalActualizarCreditoWhatsappComponent {
  constructor() {
    effect(() => {
      const credito = this.creditoWhatsapp();
      if (credito) {
        this.cargarDatos(credito);
      }
    });
  }
  private readonly formBuilder = inject(FormBuilder);
  private readonly creditoWhatsappService = inject(CreditoWhatsappService);
  private readonly toastService = inject(ToastService);

  sucursalId = input.required<number>();
  creditoWhatsapp = input.required<CreditoWhatsapp>();
  cerrar = output<void>();
  creditoActualizado = output<CreditoWhatsapp>();

  form = this.formBuilder.group({
    creditos: [0, [Validators.required, Validators.min(0)]],
    costo_por_mensaje: [0.01, [Validators.required, Validators.min(0.01)]],
    activo: [true, [Validators.required]],
    motivo: ['', [Validators.required, Validators.minLength(10)]],
  });

  private cargarDatos(credito: CreditoWhatsapp) {
    this.form.patchValue({
      creditos: credito.creditos,
      costo_por_mensaje: credito.costo_por_mensaje,
      activo: credito.activo,
      motivo: '',
    });
  }

  onSubmit() {
    const creditoActual = this.creditoWhatsapp();
    const valoresNormalizados = this.buildCreditoFormValues();

    const sinCambios = this.sinCambios(creditoActual, valoresNormalizados);

    if (sinCambios) {
      this.toastService.show({
        text: 'No hay cambios para guardar',
        classname: 'bg-info text-dark',
        delay: 2500,
      });
      return;
    }

    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    const { motivo } = this.form.value;

    const rq: CreditoWhatsappPayload = {
      creditos: valoresNormalizados.creditos,
      costo_por_mensaje: valoresNormalizados.costo_por_mensaje,
      activo: valoresNormalizados.activo,
      motivo: motivo || '',
    };
    this.creditoWhatsappService
      .actualizarCreditos(this.creditoWhatsapp().telefono_id, rq)
      .subscribe({
        next: (creditoActualizado) => {
          this.creditoActualizado.emit(creditoActualizado as CreditoWhatsapp);
          this.cerrar.emit();
          this.toastService.show({
            text: 'Crédito de WhatsApp actualizado exitosamente.',
            classname: 'bg-green-500 text-white',
            delay: 3000,
          });
        },
        error: (error) => {
          this.toastService.show({
            text: 'Error al actualizar el crédito de WhatsApp. Intenta nuevamente.',
            classname: 'bg-red-500 text-white',
            delay: 5000,
          });
          console.error('Error al actualizar crédito de WhatsApp:', error);
        },
      });
  }
  onCancelar() {
    this.cerrar.emit();
  }

  private buildCreditoFormValues(): {
    creditos: number;
    costo_por_mensaje: number;
    activo: boolean;
  } {
    return {
      creditos: Number(this.form.value.creditos),
      costo_por_mensaje: Number(this.form.value.costo_por_mensaje),
      activo: Boolean(this.form.value.activo),
    };
  }

  private sinCambios(
    creditoActual: CreditoWhatsapp,
    formValues: {
      creditos: number;
      costo_por_mensaje: number;
      activo: boolean;
    },
  ): boolean {
    return (
      formValues.creditos === creditoActual.creditos &&
      formValues.costo_por_mensaje === creditoActual.costo_por_mensaje &&
      formValues.activo === creditoActual.activo
    );
  }
}
